import { FC, Fragment } from "react"
import { useNavigate } from "react-router-dom"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined"
import Accordion from "@mui/material/Accordion"
import AccordionSummary from "@mui/material/AccordionSummary"

import { Tab, Tabs, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"

import { AccordionSidebarItemProps, BaseSidebarItemProps, TagProps } from "../../../types/Custom/Interfaces"
import { getActiveScreen } from "../helpers"

import styles from "./SidebarItem.module.scss"

/**
 * Union type for sidebar item props that can be either a base item or an accordion item
 */
export type SidebarItemProps = BaseSidebarItemProps | AccordionSidebarItemProps

/**
 * Type guard to check if props are for an accordion item
 * @param props - The props to check
 * @returns True if props contain accordionItems array
 */
const isAccordionItem = (props: SidebarItemProps): props is AccordionSidebarItemProps => {
  return "accordionItems" in props && Array.isArray(props.accordionItems)
}

/**
 * Component that renders a tag indicating no permission to access a feature
 */
const NoPermissionTag: FC = () => (
  <Tooltip title="You do not have permission to access this feature. Contact your admin for access" placement="top">
    <Tag color="gray" size="xsmall">
      <LockPersonOutlinedIcon className={styles.tagIcon} />
    </Tag>
  </Tooltip>
)

/**
 * Component that renders status tags based on feature state
 * @param props - Component props
 * @param props.isLocked - Whether the feature is locked
 * @param props.hasPermission - Whether user has permission to access
 * @param props.tag - Optional custom tag to display
 */
const FeatureTag: FC<{
  isLocked?: boolean
  hasPermission: boolean
  tag?: TagProps
}> = ({ isLocked, hasPermission, tag }) => {
  if (isLocked) {
    return (
      <Tag color="orange" size="xsmall">
        <LockOutlinedIcon className={styles.tagIcon} />
      </Tag>
    )
  }
  if (!hasPermission) {
    return <NoPermissionTag />
  }
  if (tag) {
    return (
      <Tag color={tag.color} size="xsmall">
        {tag.label}
      </Tag>
    )
  }
  return <Fragment />
}

/**
 * Component for rendering simple tab items in the sidebar
 * @param props - Component props
 * @param props.Icon - Optional icon component
 * @param props.activeScreen - Currently active screen
 * @param props.label - Tab label
 * @param props.route - Navigation route
 * @param props.subRoutes - Optional sub-routes
 * @param props.hasPermission - Whether user has permission
 * @param props.isLocked - Whether feature is locked (default is false)
 * @param props.tag - Optional tag to display
 */
const TabSidebarItem: FC<BaseSidebarItemProps> = ({
  Icon,
  activeScreen,
  label,
  route,
  subRoutes = [],
  hasPermission,
  isLocked = false,
  tag,
}) => {
  const navigate = useNavigate()

  const isActive = route
    ? getActiveScreen(route) === activeScreen ||
      subRoutes.some((subRoute) => getActiveScreen(subRoute) === activeScreen)
    : false

  return (
    <Tabs orientation="vertical" activeTab={activeScreen}>
      <Tab
        id={`${label}-tab`}
        value={label}
        selected={isActive}
        label={
          <div className={styles.accordionItem}>
            {label}
            <FeatureTag isLocked={isLocked} hasPermission={hasPermission} tag={tag} />
          </div>
        }
        onClick={() => route && navigate(`/${route}`)}
        icon={Icon ? () => <Icon className={`${styles.icon} ${isActive ? styles.selectedIcon : ""}`} /> : undefined}
        orientation="vertical"
        size="large"
        disabled={!hasPermission && !isLocked}
      />
    </Tabs>
  )
}

/**
 * Component for rendering accordion items in the sidebar
 * @param props - Component props
 * @param props.Icon - Optional icon component
 * @param props.activeScreen - Currently active screen
 * @param props.label - Accordion label
 * @param props.accordionItems - Array of items in accordion
 * @param props.isLocked - Whether feature is locked (default is false)
 * @param props.hasPermission - Whether user has permission
 * @param props.tag - Optional tag to display
 */
const AccordionSidebarItem: FC<AccordionSidebarItemProps> = ({
  Icon,
  activeScreen,
  label,
  accordionItems,
  isLocked = false,
  hasPermission,
  tag,
}) => {
  const navigate = useNavigate()

  const isAnyItemActive = accordionItems?.some(
    (item) =>
      (item.route && getActiveScreen(item.route) === activeScreen) ||
      (item.subRoutes?.some((subRoute) => getActiveScreen(subRoute) === activeScreen) ?? false)
  )

  return (
    <Accordion className={styles.accordion} defaultExpanded={true}>
      <AccordionSummary
        className={styles.dropDownContent}
        expandIcon={<ExpandMoreIcon sx={{ color: "var(--selected-icon-secondary)" }} />}
        classes={{ expanded: styles.expanded, root: styles.expanded, content: styles.content }}
        aria-controls={`${label}-content`}
        id={`${label}-header`}
      >
        {Icon && <Icon className={`${styles.icon} ${isAnyItemActive ? styles.selectedIcon : ""}`} />}
        <Typography color={isAnyItemActive ? "important" : "base"} variantColor={2} variant="a">
          {label}
        </Typography>
        <FeatureTag isLocked={isLocked} hasPermission={hasPermission} tag={tag} />
      </AccordionSummary>
      <div className={styles.itemsWrapper}>
        <Tabs orientation="vertical" value={activeScreen}>
          {accordionItems?.map((item, index) => {
            const isItemActive = item.route
              ? getActiveScreen(item.route) === activeScreen ||
                (item.subRoutes?.some((subRoute) => getActiveScreen(subRoute) === activeScreen) ?? false)
              : false

            return (
              <Tab
                key={`${item.label}-${index}`}
                id={`${item.label}-tab`}
                value={item.label}
                selected={isItemActive}
                label={
                  <div className={styles.accordionItem}>
                    {item.label}
                    <FeatureTag isLocked={item.isLocked} hasPermission={item.hasPermission} tag={item.tag} />
                  </div>
                }
                onClick={() => item.route && navigate(`/${item.route}`)}
                orientation="vertical"
                size="large"
                disabled={!item.hasPermission && !item.isLocked}
              />
            )
          })}
        </Tabs>
      </div>
    </Accordion>
  )
}

/**
 * Main component that renders either a tab or accordion sidebar item based on props
 * @param props - Component props
 * @returns Rendered sidebar item component
 */
const SidebarItem: FC<SidebarItemProps> = (props: SidebarItemProps): JSX.Element => {
  if (isAccordionItem(props)) {
    return <AccordionSidebarItem {...props} />
  }
  return <TabSidebarItem {...props} />
}

export default SidebarItem
